/**
 * Blocking overlay shown while an org owner switches clinics.
 *
 * ## Why this is a step list and not a spinner
 *
 * A clinic switch is the one action in the app that deliberately DESTROYS
 * local data: it wipes every PHI table in IndexedDB so clinic A's patients
 * cannot surface in clinic B's session. That is a big thing to do silently
 * behind a generic spinner. If it stalls or fails halfway, the user needs to
 * know exactly how far it got — "was my data cleared?" has a real answer and
 * this overlay gives it.
 *
 * Every tick here is driven by an awaited operation in ClinicContext, never by
 * a timer. A cosmetic progress animation would claim the cache was cleared
 * before it actually was, which on this particular screen is a lie with
 * clinical consequences.
 *
 * It also blocks interaction on purpose. Mid-switch the database is being
 * emptied and refilled; a click that queued a write during that window would
 * land against a half-scoped session.
 */

import React from "react";

import {
  shortClinicName,
  SwitchStep,
  useClinic,
} from "../../contexts/ClinicContext";

const StepIcon: React.FC<{ status: SwitchStep["status"] }> = ({ status }) => {
  if (status === "done") {
    return (
      <svg
        className="h-4 w-4 flex-shrink-0 text-emerald-500"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  if (status === "failed") {
    return (
      <svg
        className="h-4 w-4 flex-shrink-0 text-rose-500"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  if (status === "active") {
    return (
      <span
        className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600"
        aria-hidden="true"
      />
    );
  }
  // Not started yet — hollow ring, matching the dimmed row in the design.
  return (
    <span
      className="h-4 w-4 flex-shrink-0 rounded-full border-2 border-slate-200"
      aria-hidden="true"
    />
  );
};

/**
 * Shown in every OTHER tab once one tab switches clinics. Deliberately has no
 * way out but reloading: this browser's offline cache now holds the new
 * clinic, so this tab can no longer show — or save into — the one it was on.
 */
const StaleTabLock: React.FC<{ toName: string | null; fromName: string }> = ({
  toName,
  fromName,
}) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/20 px-4 backdrop-blur-[2px]"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="stale-tab-title"
  >
    <div
      className="w-full max-w-[380px] rounded-2xl bg-white px-6 py-5"
      style={{
        boxShadow:
          "0 12px 48px 0 rgba(15,23,42,0.18), 0 2px 8px 0 rgba(15,23,42,0.08)",
      }}
    >
      <h2
        id="stale-tab-title"
        className="text-[15px] font-semibold leading-snug text-slate-900"
      >
        Clinic changed in another tab
      </h2>
      <p className="mt-1 text-[12px] leading-snug text-slate-600">
        This browser now works in {toName ?? "a different clinic"}, so this tab
        can’t stay in {fromName} — patient records from the two clinics would
        mix.
      </p>
      <p className="mt-2 text-[12px] leading-snug text-slate-500">
        Everything you already saved is safe. Anything typed in this tab but not
        yet saved will be lost.
      </p>
      <div className="mt-4 flex justify-end border-t border-slate-100 pt-3">
        <button
          onClick={() => window.location.assign("/dashboard")}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-slate-700"
        >
          {toName ? `Continue in ${toName}` : "Reload"}
        </button>
      </div>
    </div>
  </div>
);

export const ClinicSwitchOverlay: React.FC = () => {
  const { switchProgress, dismissSwitch, staleTab, activeClinic } = useClinic();

  if (staleTab) {
    return (
      <StaleTabLock
        toName={staleTab.toClinic ? shortClinicName(staleTab.toClinic) : null}
        fromName={shortClinicName(activeClinic)}
      />
    );
  }

  if (!switchProgress) return null;

  const { toName, toShortName, fromShortName, steps, error } = switchProgress;
  const failed = error !== null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/20 px-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-busy={!failed}
      aria-label={`Switching to ${toName}`}
    >
      <div
        className="w-full max-w-[380px] rounded-2xl bg-white px-6 py-5"
        style={{
          boxShadow:
            "0 12px 48px 0 rgba(15,23,42,0.18), 0 2px 8px 0 rgba(15,23,42,0.08)",
        }}
      >
        <div className="flex items-start gap-3">
          {failed ? (
            <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-rose-100">
              <svg
                className="h-3 w-3 text-rose-600"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          ) : (
            <span
              className="mt-0.5 h-5 w-5 flex-shrink-0 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600"
              aria-hidden="true"
            />
          )}
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold leading-snug text-slate-900">
              {failed ? "Could not switch clinic" : `Switching to ${toName}`}
            </h2>
            <p className="mt-0.5 text-[12px] leading-snug text-slate-500">
              {failed
                ? error
                : fromShortName
                  ? `${fromShortName} data is being cleared from this device before ${toShortName} loads.`
                  : `Preparing ${toShortName} on this device.`}
            </p>
          </div>
        </div>

        {/* aria-live so a screen reader announces each tick as it lands. */}
        <ul className="mt-4 space-y-2.5" aria-live="polite">
          {steps.map((step) => (
            <li key={step.id} className="flex items-start gap-2.5">
              <span className="mt-[1px]">
                <StepIcon status={step.status} />
              </span>
              <span
                className={`text-[12px] leading-snug ${
                  step.status === "done"
                    ? "text-slate-700"
                    : step.status === "active"
                      ? "font-medium text-slate-900"
                      : step.status === "failed"
                        ? "font-medium text-rose-700"
                        : "text-slate-400"
                }`}
              >
                {step.label}
              </span>
            </li>
          ))}
        </ul>

        {failed ? (
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
            {/*
              A switch that failed AFTER the flush step left this device with an
              empty cache. Say so plainly rather than letting the user discover
              an empty patient list on their own.
            */}
            <p className="text-[11px] leading-snug text-slate-500">
              {steps.some((s) => s.id === "flush" && s.status === "done")
                ? "Local data was already cleared. Reconnect and reload to rebuild it."
                : "Nothing on this device was changed."}
            </p>
            <button
              onClick={dismissSwitch}
              className="flex-shrink-0 rounded-lg bg-slate-900 px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-slate-700"
            >
              Close
            </button>
          </div>
        ) : (
          <p className="mt-4 border-t border-slate-100 pt-3 text-[11px] leading-snug text-slate-400">
            The offline cache holds one clinic at a time, so it is cleared and
            rebuilt on every switch. Please keep this tab open.
          </p>
        )}
      </div>
    </div>
  );
};

export default ClinicSwitchOverlay;
